import React from "react";
import {
  Box,
  Typography,
  FormControl,
  FormControlLabel,
  RadioGroup,
  Radio,
  Chip,
  Button,
  ToggleButton,
  ToggleButtonGroup,
  TextField,
} from "@mui/material";
import { useSelector } from "react-redux";
import ManagerBodyMapBoy from "../BodyMapSafeGuardingManager/manager-body-map-boy";
import ManagerBodyMapGirl from "../BodyMapSafeGuardingManager/manager-body-map-girl";

const ToggleButtons = ({ label, value }) => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      mb: 3,
    }}
  >
    <Typography sx={{ color: "#666", fontWeight: 500, flex: 1, pr: 4 }}>{label}</Typography>
    <ToggleButtonGroup value={value === null ? "" : value ? "yes" : "no"} exclusive size='small'>
      <ToggleButton
        value='yes'
        sx={{
          "&.MuiToggleButton-root": {
            border: "1px solid #ddd",
            px: 3,
            "&.Mui-selected": {
              bgcolor: "primary.main",
              color: "white",
              "&:hover": {
                bgcolor: "primary.dark",
              },
            },
            "&:hover": {
              bgcolor: "#f9f9f9",
            },
          },
        }}
      >
        Yes
      </ToggleButton>
      <Box sx={{ width: 16 }} />
      <ToggleButton
        value='no'
        sx={{
          "&.MuiToggleButton-root": {
            border: "1px solid #ddd",
            px: 3,
            "&.Mui-selected": {
              bgcolor: "error.main",
              color: "white",
              "&:hover": {
                bgcolor: "error.dark",
              },
            },
            "&:hover": {
              bgcolor: "#f9f9f9",
            },
          },
        }}
      >
        No
      </ToggleButton>
    </ToggleButtonGroup>
  </Box>
);

const NonPhysicalIncidentManager = ({
  physicaldata,
  prevStep,
  handleSubmit,
  incidentCategory,
  uploadedImages,
  gender,
}) => {
  const user = useSelector((state) => state.auth.user); // User data from Redux store

  return (
    <Box sx={{ pl: 4, pr: 4, ml: 2 }}>
      <Chip
        variant='contained'
        sx={{
          fontSize: "14px",
          backgroundColor: "#F2F6FF",
          color: "blue",
        }}
        label='Daily Log'
        size='small'
      />
      <Typography sx={{ fontSize: "24px", color: "black", fontWeight: "bold" }}>Non - Physical Intervention</Typography>
      <Typography variant='subtitle2' sx={{ color: "#757575" }}>
        Date: {new Date().toLocaleDateString()} &nbsp; • &nbsp;{" "}
        <span style={{ color: "#1751D0" }}>{user.username}</span>
      </Typography>
      <Box sx={{ borderBottom: "1px solid #E0E0E0", mt: 2, mb: 2 }} />

      {/* Incident Category */}
      <Typography sx={{ fontSize: "14px", fontWeight: "bold", mb: 2 }}>
        Did the young person sustain any injuries ?*
      </Typography>
      <FormControl component='fieldset' ml={2}>
        <RadioGroup row value={incidentCategory}>
          <FormControlLabel
            value='Yes'
            control={<Radio sx={{ color: "#007AFF" }} />}
            label={
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <Typography sx={{ fontSize: "14px", fontWeight: "bold", color: "black" }}>YES</Typography>
                <Typography sx={{ fontSize: "12px", color: "#A0A0A0" }}>Show them in the body map</Typography>
              </Box>
            }
            sx={{
              borderRadius: "10px",
              padding: "10px 15px",
              border: "1px solid #007AFF",
              width: "fit-content",
            }}
          />
          <FormControlLabel
            value='No'
            control={<Radio sx={{ color: "#007AFF" }} />}
            label={
              <Box>
                <Typography sx={{ fontSize: "14px", fontWeight: "bold", color: "black" }}>No</Typography>
                <Typography sx={{ fontSize: "12px", color: "#A0A0A0" }}>There is no injuries happed</Typography>
              </Box>
            }
            sx={{
              borderRadius: "10px",
              padding: "10px 15px",
              border: "1px solid #007AFF",
              width: "fit-content",
            }}
          />
        </RadioGroup>
      </FormControl>

      {incidentCategory === "Yes" && (
        <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, gap: 4 }}>
          {/* Left Column - Body Map */}
          <Box sx={{ flex: 1 }}>
            {gender === "Male" ? (
              <ManagerBodyMapBoy onUpload={() => {}} uploadedImages={uploadedImages} />
            ) : gender === "Female" ? (
              <ManagerBodyMapGirl onUpload={() => {}} uploadedImages={uploadedImages} />
            ) : (
              <></>
            )}
          </Box>

          <Box sx={{ flex: 1 }}>
            <Box sx={{ maxWidth: 600 }}>
              <ToggleButtons
                label='Was medical attention required within 24 hours?'
                value={physicaldata?.medicalAttentionRequired}
              />

              <ToggleButtons label='Was medical attention offered?' value={physicaldata?.medicalAttentionOffered} />

              <Box sx={{ mb: 3 }}>
                <Typography sx={{ color: "#666", fontWeight: 500, mb: 1 }}>
                  Time medical attention offered (24hr)
                </Typography>
                <TextField
                  type='time'
                  value={physicaldata?.timeOfAttention}
                  variant='outlined'
                  size='small'
                  sx={{
                    width: 170,
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 1,
                    },
                  }}
                />
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography sx={{ color: "#666", fontWeight: 500, mb: 1 }}>Who provided medical attention?</Typography>
                <TextField
                  value={physicaldata?.provider}
                  variant='outlined'
                  size='small'
                  sx={{
                    width: 170,
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 1,
                    },
                  }}
                />
              </Box>

              <ToggleButtons
                label='Has the Young Person Complained of any injury or discomfort?'
                value={physicaldata?.complained}
              />

              <ToggleButtons label='Are there any visual signs of injury?' value={physicaldata?.visualSigns} />
            </Box>
          </Box>
        </Box>
      )}

      {/* Footer Actions */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 2,
          position: "sticky",
          bottom: 0,
          padding: 2,
          zIndex: 10,
        }}
      >
        <Button
          variant='outlined'
          color='primary'
          onClick={() => {
            window.scrollTo(0, 0); // Prevent scrolling
            prevStep();
          }}
        >
          Previous
        </Button>

        <Button
          variant='contained'
          color='primary'
          onClick={() => {
            window.scrollTo(0, 0); // Prevent scrolling
            handleSubmit();
          }}
        >
          Close
        </Button>
      </Box>
    </Box>
  );
};

export default NonPhysicalIncidentManager;
